import { useCallback, useEffect } from "react";
import { nanoid } from "nanoid";
import { useTemplatesStatusStore } from "../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { duplicateItem, findItemByTypeAndId } from "../helpers/itemsHelper";
import { getShapeId } from "../components/StoryboardMenuTabs/ShapesTab/helpers";
import { useChangeLayer } from "./useChangeLayer";
import { useMainImageAssetPath } from "./useMainImageAssetPath";
import { ITEM_TYPES } from "../types";

/**
 * Copy/paste behaviour for the currently selected template item.
 *
 * Extracted from the (now removed in the video editor) settings toolbar so the
 * side-menu tabs can reuse the exact same logic. `registerShortcuts` wires the
 * Cmd/Ctrl+C / Cmd/Ctrl+V keyboard listener and must only be enabled on a
 * single, always-mounted consumer per surface to avoid duplicate handling.
 */
export const useItemCopyPaste = ({
  previewWidth,
  previewHeight,
  disableCopyPaste = false,
  registerShortcuts = false,
}: {
  previewWidth: number;
  previewHeight: number;
  disableCopyPaste?: boolean;
  registerShortcuts?: boolean;
}) => {
  const { copiedItem, setCopiedItem, addTemplateUpdate } =
    useTemplatesStatusStore((state) => ({
      copiedItem: state.copiedItem,
      setCopiedItem: state.setCopiedItem,
      addTemplateUpdate: state.addTemplateUpdate,
    }));

  const {
    currentItem,
    templateState,
    addCustomText,
    addShape,
    addImage,
    addVideo,
  } = useTemplateEditorStore((store) => ({
    currentItem: store.templateState.currentItem,
    templateState: store.templateState,
    addCustomText: store.addCustomText,
    addShape: store.addShape,
    addImage: store.addImage,
    addVideo: store.addVideo,
  }));

  const { newHighestLayerIndex } = useChangeLayer();

  const { currentPath, mediaType: mainImageMediaType } =
    useMainImageAssetPath();

  const handleCopyItem = useCallback(() => {
    if (!currentItem || disableCopyPaste) return;
    const item = findItemByTypeAndId(currentItem, templateState);
    if (!item) return;
    if (item.type === ITEM_TYPES.Image || item.type === ITEM_TYPES.Video) {
      const isMainImage = templateState.mainImage?.id === item.id;
      const isMainImageVideo = isMainImage && mainImageMediaType === "video";
      setCopiedItem({
        ...item,
        type: isMainImageVideo ? ITEM_TYPES.Video : ITEM_TYPES.Image,
        path: currentPath,
      });
    } else {
      setCopiedItem({ ...item });
    }
  }, [
    currentItem,
    setCopiedItem,
    templateState,
    disableCopyPaste,
    currentPath,
    mainImageMediaType,
  ]);

  const handlePasteItem = useCallback(() => {
    if (disableCopyPaste) return;
    if (!copiedItem) return;
    switch (copiedItem.type) {
      case ITEM_TYPES.CustomText: {
        const isCustomTextInCurrentTemplate = templateState.customTexts.find(
          (text) => text.id === copiedItem.id
        );
        const newTextId = nanoid();
        if (isCustomTextInCurrentTemplate) {
          duplicateItem(
            copiedItem,
            addCustomText,
            previewWidth,
            previewHeight,
            newHighestLayerIndex,
            newTextId
          );
        } else {
          const { type, ...rest } = copiedItem;
          addCustomText({
            ...rest,
            id: newTextId,
            layerIndex: newHighestLayerIndex,
          });
        }
        break;
      }
      case ITEM_TYPES.Shape: {
        const isShapeInCurrentTemplate = templateState.shapes.find(
          (shape) => shape.id === copiedItem.id
        );
        const newShapeId = getShapeId(copiedItem.shapeType);
        if (isShapeInCurrentTemplate) {
          duplicateItem(
            copiedItem,
            addShape,
            previewWidth,
            previewHeight,
            newHighestLayerIndex,
            newShapeId
          );
        } else {
          const { type, ...rest } = copiedItem;
          addShape({
            ...rest,
            id: newShapeId,
            layerIndex: newHighestLayerIndex,
          });
        }
        break;
      }
      case ITEM_TYPES.Image: {
        const isImageInCurrentTemplate = templateState.images.find(
          (image) => image.id === copiedItem.id
        );
        const newImageId = nanoid();
        if (isImageInCurrentTemplate) {
          duplicateItem(
            copiedItem,
            addImage,
            previewWidth,
            previewHeight,
            newHighestLayerIndex,
            newImageId
          );
        } else {
          const { type, ...rest } = copiedItem;
          addImage({
            ...rest,
            id: newImageId,
            layerIndex: newHighestLayerIndex,
          });
        }
        break;
      }
      case ITEM_TYPES.Video: {
        const isVideoInCurrentTemplate = templateState.videos.find(
          (video) => video.id === copiedItem.id
        );
        const newVideoId = nanoid();
        if (isVideoInCurrentTemplate) {
          duplicateItem(
            copiedItem,
            addVideo,
            previewWidth,
            previewHeight,
            newHighestLayerIndex,
            newVideoId
          );
        } else {
          const { type, ...rest } = copiedItem;
          addVideo({
            ...rest,
            id: newVideoId,
            layerIndex: newHighestLayerIndex,
          });
        }
        break;
      }
      case ITEM_TYPES.Logo: {
        if (!copiedItem.assetId) return;
        const isLogoInCurrentTemplate =
          templateState.logo.assetId === copiedItem.assetId;
        const { type, showLogo, ...rest } = copiedItem;
        const newImage = { id: nanoid(), ...rest };
        if (isLogoInCurrentTemplate) {
          duplicateItem(
            { ...newImage, assetId: copiedItem.assetId },
            addImage,
            previewWidth,
            previewHeight,
            newHighestLayerIndex,
            newImage.id
          );
        } else {
          addImage({
            ...newImage,
            assetId: copiedItem.assetId,
            layerIndex: newHighestLayerIndex,
          });
        }
      }
    }
    addTemplateUpdate();
  }, [
    addCustomText,
    addImage,
    addShape,
    addTemplateUpdate,
    addVideo,
    copiedItem,
    previewHeight,
    previewWidth,
    templateState.customTexts,
    templateState.images,
    templateState.shapes,
    templateState.videos,
    templateState.logo,
    newHighestLayerIndex,
    disableCopyPaste,
  ]);

  useEffect(() => {
    if (!registerShortcuts) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      )
        return;
      if (event.key === "c" && (event.metaKey || event.ctrlKey)) {
        handleCopyItem();
      }
      if (event.key === "v" && (event.metaKey || event.ctrlKey)) {
        handlePasteItem();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleCopyItem, handlePasteItem, registerShortcuts]);

  const canCopy =
    !disableCopyPaste &&
    !!currentItem &&
    !!findItemByTypeAndId(currentItem, templateState);
  const canPaste = !disableCopyPaste && !!copiedItem;

  return { handleCopyItem, handlePasteItem, canCopy, canPaste };
};
